import { Alert, Box, Button, Card, CardHeader, CircularProgress, Divider } from '@mui/material';
import type { CardProps } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useId, type ReactNode } from 'react';

type CommonProps = Omit<CardProps, 'children' | 'title'> & {
  title: string;
  subheader?: string;
  height: number;
};

export type ChartCardProps = CommonProps &
  (
    | { state: 'loading' | 'empty'; children?: never; message?: never; onRetry?: never }
    | { state: 'error'; message: string; onRetry: () => void; children?: never }
    | { state: 'ready'; children: ReactNode; message?: never; onRetry?: never }
  );

export const ChartCard = ({ title, subheader, height, state, children, message, onRetry, ...cardProps }: ChartCardProps) => {
  const titleId = useId();

  return (
    <Card component='section' aria-labelledby={titleId} {...cardProps}>
      <CardHeader sx={{ p: 2 }} title={<span id={titleId}>{title}</span>} subheader={subheader} />
      <Divider />
      <Box
        sx={{
          minWidth: 500,
          minHeight: height,
          width: '100%',
          boxSizing: 'border-box',
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: state === 'ready' ? 'flex-start' : 'center',
          justifyContent: state === 'ready' ? 'flex-start' : 'center',
        }}
      >
        {state === 'loading' && (
          <Box role='status'>
            <CircularProgress size={24} />
            <Box component='span' sx={visuallyHidden}>{`Loading ${title}`}</Box>
          </Box>
        )}
        {state === 'empty' && <Box sx={{ color: 'text.secondary' }}>No data</Box>}
        {state === 'error' && (
          <Alert
            severity='error'
            action={
              <Button color='inherit' size='small' aria-label={`Retry ${title}`} onClick={onRetry}>
                Retry
              </Button>
            }
          >
            {message}
          </Alert>
        )}
        {state === 'ready' && <Box sx={{ width: '100%' }}>{children}</Box>}
      </Box>
    </Card>
  );
};
